if (!customElements.get('td-subscription-selector')) {
  customElements.define(
    'td-subscription-selector',
    class TdSubscriptionSelector extends HTMLElement {
      connectedCallback() {
        this.formId = this.dataset.productFormId;
        this.form = document.getElementById(this.formId);
        this.variantData = this.parseVariantData();
        this.defaultPurchaseType = this.dataset.defaultPurchaseType || 'subscription';
        this.hideDetailsWhenOnetimeMobile = this.dataset.hideDetailsWhenOnetimeMobile === 'true';
        this.useCompareAtSavings = this.dataset.useCompareAtSavings === 'true';
        this.savingsBadgeLabel = this.dataset.savingsBadgeLabel || 'Save';
        this.mobileMediaQuery = window.matchMedia('(max-width: 767px)');

        this.purchaseButtons = Array.from(this.querySelectorAll('[data-td-purchase-type]'));
        this.subscriptionCard = this.querySelector('[data-td-card="subscription"]');
        this.onetimeCard = this.querySelector('[data-td-card="onetime"]');
        this.subscriptionToggle = this.querySelector('[data-td-purchase-type="subscription"]');
        this.planSelect = this.querySelector('[data-td-selling-plan-select]');
        this.subscriptionDetails = this.querySelector('[data-td-subscription-details]');
        this.subscriptionHint = this.querySelector('[data-td-subscription-hint]');
        this.subscriptionDiscountLabel = this.querySelector('[data-td-subscription-discount]');
        this.onetimeDiscountLabel = this.querySelector('[data-td-onetime-discount]');
        this.subscriptionPrice = this.querySelector('[data-td-subscription-price]');
        this.subscriptionCompare = this.querySelector('[data-td-subscription-compare]');
        this.onetimePrice = this.querySelector('[data-td-onetime-price]');
        this.onetimeCompare = this.querySelector('[data-td-onetime-compare]');
        this.submitButton = this.form?.querySelector('[name="add"]');
        this.submitButtonLabel = this.submitButton?.querySelector('[data-td-button-label]');
        this.submitButtonPricing = this.submitButton?.querySelector('[data-td-button-pricing]');
        this.submitButtonPrice = this.submitButton?.querySelector('[data-td-button-price]');
        this.submitButtonCompare = this.submitButton?.querySelector('[data-td-button-compare]');
        this.quantityInput = this.form?.querySelector('input[name="quantity"]');

        this.onPurchaseTypeClick = this.onPurchaseTypeClick.bind(this);
        this.onSellingPlanChange = this.onSellingPlanChange.bind(this);
        this.onFormSubmit = this.onFormSubmit.bind(this);
        this.onVariantChanged = this.onVariantChanged.bind(this);
        this.onViewportChange = this.onViewportChange.bind(this);
        this.onQuantityChange = this.onQuantityChange.bind(this);
        this.onPurchaseTypeKeydown = this.onPurchaseTypeKeydown.bind(this);
        this.releaseSubscriptionTransitionLock = this.releaseSubscriptionTransitionLock.bind(this);

        this.purchaseButtons.forEach((button) => {
          button.addEventListener('click', this.onPurchaseTypeClick);
          button.addEventListener('keydown', this.onPurchaseTypeKeydown);
        });

        if (this.planSelect) {
          this.planSelect.addEventListener('change', this.onSellingPlanChange);
        }

        if (this.form) {
          this.form.addEventListener('submit', this.onFormSubmit);
        }

        if (this.quantityInput) {
          this.quantityInput.addEventListener('change', this.onQuantityChange);
          this.quantityInput.addEventListener('input', this.onQuantityChange);
        }

        const quantityUpdateEvent = window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.quantityUpdate;
        if (quantityUpdateEvent && window.FoxTheme?.pubsub?.subscribe) {
          this.quantityPubsubUnsubscriber = window.FoxTheme.pubsub.subscribe(
            quantityUpdateEvent,
            this.onQuantityChange
          );
        }

        document.addEventListener('variant:changed', this.onVariantChanged);
        if (this.mobileMediaQuery?.addEventListener) {
          this.mobileMediaQuery.addEventListener('change', this.onViewportChange);
        } else if (this.mobileMediaQuery?.addListener) {
          this.mobileMediaQuery.addListener(this.onViewportChange);
        }

        this.currentVariantId = this.getCurrentVariantId();
        this.purchaseType = this.defaultPurchaseType;
        this.sync();
      }

      disconnectedCallback() {
        if (this.transitionLockFrame) {
          cancelAnimationFrame(this.transitionLockFrame);
        }

        this.purchaseButtons?.forEach((button) => {
          button.removeEventListener('click', this.onPurchaseTypeClick);
          button.removeEventListener('keydown', this.onPurchaseTypeKeydown);
        });

        if (this.planSelect) {
          this.planSelect.removeEventListener('change', this.onSellingPlanChange);
        }

        if (this.form) {
          this.form.removeEventListener('submit', this.onFormSubmit);
        }

        if (this.quantityInput) {
          this.quantityInput.removeEventListener('change', this.onQuantityChange);
          this.quantityInput.removeEventListener('input', this.onQuantityChange);
        }

        if (this.quantityPubsubUnsubscriber) {
          this.quantityPubsubUnsubscriber();
          this.quantityPubsubUnsubscriber = null;
        }

        document.removeEventListener('variant:changed', this.onVariantChanged);
        if (this.mobileMediaQuery?.removeEventListener) {
          this.mobileMediaQuery.removeEventListener('change', this.onViewportChange);
        } else if (this.mobileMediaQuery?.removeListener) {
          this.mobileMediaQuery.removeListener(this.onViewportChange);
        }
      }

      parseVariantData() {
        const jsonElement = this.querySelector('[data-td-variant-data]');
        if (!jsonElement) return {};

        try {
          return JSON.parse(jsonElement.textContent);
        } catch (error) {
          console.error('Unable to parse td subscription selector variant data.', error);
          return {};
        }
      }

      getCurrentVariantId() {
        const variantId = this.form?.querySelector('input[name="id"]')?.value || this.dataset.variantId;
        return variantId ? String(variantId) : null;
      }

      getCurrentVariantData() {
        if (this.currentVariantId && this.variantData[this.currentVariantId]) {
          return this.variantData[this.currentVariantId];
        }

        const [firstVariantId] = Object.keys(this.variantData);
        this.currentVariantId = firstVariantId || null;
        return firstVariantId ? this.variantData[firstVariantId] : null;
      }

      onPurchaseTypeClick(event) {
        const button = event.currentTarget;
        const purchaseType = button.dataset.tdPurchaseType;
        if (!purchaseType) return;

        if (purchaseType === 'subscription' && !this.hasSellingPlans()) {
          return;
        }

        this.purchaseType = purchaseType;
        this.sync();
      }

      onPurchaseTypeKeydown(event) {
        const navigationKeys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
        if (!navigationKeys.includes(event.key)) return;

        event.preventDefault();

        const enabledButtons = this.purchaseButtons.filter((button) => !button.disabled);
        if (!enabledButtons.length) return;

        const currentIndex = enabledButtons.indexOf(event.currentTarget);
        if (currentIndex === -1) return;

        let nextIndex = currentIndex;

        if (event.key === 'Home') {
          nextIndex = 0;
        } else if (event.key === 'End') {
          nextIndex = enabledButtons.length - 1;
        } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          nextIndex = (currentIndex + 1) % enabledButtons.length;
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          nextIndex = (currentIndex - 1 + enabledButtons.length) % enabledButtons.length;
        }

        const nextButton = enabledButtons[nextIndex];
        if (!nextButton) return;

        const nextPurchaseType = nextButton.dataset.tdPurchaseType;
        if (!nextPurchaseType) return;

        this.purchaseType = nextPurchaseType;
        this.sync();
        nextButton.focus();
      }

      onSellingPlanChange() {
        const selectedPlan = this.updateSubscriptionPrice();
        this.updateSubmitButton(this.getCurrentVariantData(), selectedPlan);
        this.syncFormSellingPlan();
      }

      onFormSubmit() {
        this.syncFormSellingPlan();
      }

      onQuantityChange() {
        const variantData = this.getCurrentVariantData();
        if (!variantData) return;

        const selectedPlan = this.dataset.purchaseType === 'subscription' ? this.getSelectedPlan(variantData) : null;
        this.updateSubmitButton(variantData, selectedPlan);
      }

      onVariantChanged(event) {
        const variantId = event?.detail?.variant?.id;
        if (!variantId) return;

        const nextVariantId = String(variantId);
        if (!this.variantData[nextVariantId]) return;

        const previousHasSubscription = this.hasSellingPlans();
        const nextHasSubscription = Boolean(this.variantData[nextVariantId]?.sellingPlans?.length);
        const shouldPreserveExpandedDetails =
          previousHasSubscription &&
          nextHasSubscription &&
          this.purchaseType === 'subscription' &&
          this.isSubscriptionDetailsExpanded();

        if (previousHasSubscription !== nextHasSubscription) {
          this.lockSubscriptionTransition();
        }

        this.currentVariantId = nextVariantId;
        this.sync({
          preserveDetailsVisibility: previousHasSubscription && nextHasSubscription,
        });

        if (shouldPreserveExpandedDetails) {
          this.forceSubscriptionDetailsExpanded();
        }
      }

      onViewportChange() {
        const variantData = this.getCurrentVariantData();
        if (!variantData) return;

        this.updateCardState(variantData);
      }

      isMobileViewport() {
        return Boolean(this.mobileMediaQuery?.matches);
      }

      shouldHideDetailsWhenOnetimeOnMobile() {
        return this.hideDetailsWhenOnetimeMobile && this.isMobileViewport();
      }

      hasSellingPlans() {
        const variantData = this.getCurrentVariantData();
        return Boolean(variantData?.sellingPlans?.length);
      }

      isSubscriptionDetailsExpanded() {
        return Boolean(
          this.subscriptionDetails &&
            !this.subscriptionDetails.hidden &&
            !this.subscriptionDetails.classList.contains('is-collapsed-mobile')
        );
      }

      forceSubscriptionDetailsExpanded() {
        if (!this.subscriptionDetails) return;

        this.subscriptionDetails.hidden = false;
        this.subscriptionDetails.classList.remove('is-collapsed-mobile');

        if (this.subscriptionCard) {
          this.subscriptionCard.classList.remove('is-hidden');
          this.subscriptionCard.setAttribute('aria-hidden', 'false');
        }

        if (this.subscriptionToggle) {
          this.subscriptionToggle.setAttribute('aria-expanded', 'true');
        }
      }

      lockSubscriptionTransition() {
        this.classList.add('td-subscription-selector--no-subscription-transition');

        if (this.transitionLockFrame) {
          cancelAnimationFrame(this.transitionLockFrame);
        }

        this.transitionLockFrame = requestAnimationFrame(() => {
          this.transitionLockFrame = requestAnimationFrame(this.releaseSubscriptionTransitionLock);
        });
      }

      releaseSubscriptionTransitionLock() {
        this.classList.remove('td-subscription-selector--no-subscription-transition');
        this.transitionLockFrame = null;
      }

      sync(options = {}) {
        const variantData = this.getCurrentVariantData();
        if (!variantData) return;

        if (this.purchaseType === 'subscription' && !variantData.sellingPlans.length) {
          this.purchaseType = 'onetime';
        }

        this.renderOnetimePrice(variantData);
        this.populateSellingPlans(variantData);
        const selectedPlan = this.updateSubscriptionPrice();
        this.updateCardState(variantData, options);
        this.updateSubmitButton(variantData, selectedPlan);
        this.syncFormSellingPlan();
      }

      populateSellingPlans(variantData) {
        if (!this.planSelect) return;

        const previousValue = this.planSelect.value;
        const plans = variantData.sellingPlans || [];

        this.planSelect.innerHTML = '';

        plans.forEach((plan) => {
          const option = document.createElement('option');
          option.value = String(plan.id);
          option.textContent = plan.label || plan.name;
          this.planSelect.appendChild(option);
        });

        if (!plans.length) {
          this.planSelect.disabled = true;
          return;
        }

        this.planSelect.disabled = false;
        const matchingPlan = plans.find((plan) => String(plan.id) === previousValue) || plans[0];
        this.planSelect.value = String(matchingPlan.id);
      }

      updateCardState(variantData, options = {}) {
        const hasSubscription = variantData.sellingPlans.length > 0;
        const isSubscription = this.purchaseType === 'subscription' && hasSubscription;
        let showSubscriptionDetails = isSubscription;
        const useMobileAccordionVisual = hasSubscription && this.shouldHideDetailsWhenOnetimeOnMobile();
        const preserveDetailsVisibility = Boolean(options.preserveDetailsVisibility) && hasSubscription && this.subscriptionDetails;
        let detailsHidden = !hasSubscription;
        let detailsCollapsed = false;

        if (hasSubscription && this.isMobileViewport() && !this.shouldHideDetailsWhenOnetimeOnMobile()) {
          showSubscriptionDetails = true;
        }

        if (!detailsHidden) {
          if (preserveDetailsVisibility) {
            detailsHidden = this.subscriptionDetails.hidden;
            detailsCollapsed = this.subscriptionDetails.classList.contains('is-collapsed-mobile');
          } else if (useMobileAccordionVisual && !showSubscriptionDetails) {
            detailsHidden = false;
            detailsCollapsed = true;
          } else {
            detailsHidden = !showSubscriptionDetails;
          }
        }

        const detailsExpanded = !detailsHidden && !detailsCollapsed;

        this.dataset.purchaseType = isSubscription ? 'subscription' : 'onetime';
        this.classList.toggle('td-subscription-selector--subscription-hidden', !hasSubscription);

        this.purchaseButtons.forEach((button) => {
          const isActive = button.dataset.tdPurchaseType === this.dataset.purchaseType;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-checked', isActive ? 'true' : 'false');
          button.tabIndex = isActive ? 0 : -1;

          if (button.dataset.tdPurchaseType === 'subscription') {
            button.setAttribute('aria-disabled', hasSubscription ? 'false' : 'true');
            button.setAttribute('aria-expanded', detailsExpanded ? 'true' : 'false');
            button.disabled = !hasSubscription;
            if (!hasSubscription) {
              button.tabIndex = -1;
            }
          }
        });

        if (this.subscriptionCard) {
          this.subscriptionCard.classList.toggle('is-active', isSubscription);
          this.subscriptionCard.classList.toggle('is-hidden', !hasSubscription);
          this.subscriptionCard.setAttribute('aria-hidden', hasSubscription ? 'false' : 'true');
        }

        if (this.onetimeCard) {
          this.onetimeCard.classList.toggle('is-active', !isSubscription);
        }

        if (this.subscriptionDetails) {
          this.subscriptionDetails.hidden = detailsHidden;
          this.subscriptionDetails.classList.toggle('is-collapsed-mobile', detailsCollapsed);
        }

        if (this.subscriptionHint) {
          this.subscriptionHint.hidden = hasSubscription;
        }
      }

      renderOnetimePrice(variantData) {
        if (this.onetimePrice) {
          this.onetimePrice.textContent = variantData.oneTime.price;
        }

        if (this.onetimeCompare) {
          const shouldShowCompare = Number(variantData.oneTime.compareAtPriceValue) > Number(variantData.oneTime.priceValue);
          this.onetimeCompare.textContent = shouldShowCompare ? variantData.oneTime.compareAtPrice : '';
          this.onetimeCompare.hidden = !shouldShowCompare;
        }

        if (this.onetimeDiscountLabel) {
          if (!this.useCompareAtSavings) {
            this.onetimeDiscountLabel.textContent = '';
            this.onetimeDiscountLabel.hidden = true;
          } else {
            const comparePrice = Number(variantData.oneTime.compareAtPriceValue);
            const oneTimePrice = Number(variantData.oneTime.priceValue);
            const discountPercent = comparePrice > oneTimePrice ? Math.round(((comparePrice - oneTimePrice) / comparePrice) * 100) : 0;
            this.onetimeDiscountLabel.textContent = discountPercent > 0 ? this.formatSavingsBadgeLabel(discountPercent) : '';
            this.onetimeDiscountLabel.hidden = discountPercent <= 0;
          }
        }
      }

      updateSubscriptionPrice() {
        const variantData = this.getCurrentVariantData();
        if (!variantData?.sellingPlans?.length) {
          if (this.subscriptionPrice) this.subscriptionPrice.textContent = '';
          if (this.subscriptionCompare) {
            this.subscriptionCompare.textContent = '';
            this.subscriptionCompare.hidden = true;
          }
          if (this.subscriptionDiscountLabel) {
            this.subscriptionDiscountLabel.textContent = '';
            this.subscriptionDiscountLabel.hidden = true;
          }
          return null;
        }

        const selectedPlan = this.getSelectedPlan(variantData);

        if (this.subscriptionPrice) {
          this.subscriptionPrice.textContent = selectedPlan.price;
        }

        if (this.subscriptionCompare) {
          const compareValue = Number(selectedPlan.compareAtPriceValue);
          const shouldShowCompare = compareValue > Number(selectedPlan.priceValue);
          this.subscriptionCompare.textContent = shouldShowCompare ? selectedPlan.compareAtPrice : '';
          this.subscriptionCompare.hidden = !shouldShowCompare;
        }

        if (this.subscriptionDiscountLabel) {
          const comparePrice = this.useCompareAtSavings
            ? Number(selectedPlan.compareAtPriceValue)
            : Number(variantData.oneTime.priceValue);
          const planPrice = Number(selectedPlan.priceValue);
          const discountPercent = comparePrice > planPrice ? Math.round(((comparePrice - planPrice) / comparePrice) * 100) : 0;
          this.subscriptionDiscountLabel.textContent = discountPercent > 0 ? this.formatSavingsBadgeLabel(discountPercent) : '';
          this.subscriptionDiscountLabel.hidden = discountPercent <= 0;
        }

        return selectedPlan;
      }

      getSelectedPlan(variantData) {
        if (!variantData?.sellingPlans?.length) return null;

        const selectedPlanId = this.planSelect?.value;
        return variantData.sellingPlans.find((plan) => String(plan.id) === String(selectedPlanId)) || variantData.sellingPlans[0];
      }

      getQuantity() {
        const quantity = Number.parseInt(this.quantityInput?.value || '1', 10);
        return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
      }

      formatMoney(cents) {
        const moneyFormat = window.FoxTheme?.settings?.moneyFormat;
        if (window.FoxTheme?.Currency?.formatMoney && moneyFormat) {
          return window.FoxTheme.Currency.formatMoney(cents, moneyFormat);
        }

        const currencyCode =
          window.Shopify?.currency?.active ||
          window.Shopify?.currency?.default ||
          'USD';
        const locale = document.documentElement.lang || navigator.language || 'en-US';

        try {
          return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currencyCode,
          }).format(Number(cents) / 100);
        } catch (error) {
          return `$${(Number(cents) / 100).toFixed(2)}`;
        }
      }

      formatSavingsBadgeLabel(discountPercent) {
        return `${this.savingsBadgeLabel} ${discountPercent}%`;
      }

      updateSubmitButton(variantData, selectedPlan = null) {
        this.updateSubmitButtonCopy();

        if (!this.submitButtonPricing || !variantData) return;

        const isSubscription = this.dataset.purchaseType === 'subscription' && selectedPlan;
        const pricingData = isSubscription ? selectedPlan : variantData.oneTime;
        if (!pricingData) return;
        const quantity = this.getQuantity();
        const totalPrice = Number(pricingData.priceValue) * quantity;
        const totalComparePrice = Number(pricingData.compareAtPriceValue) * quantity;

        if (this.submitButtonPrice) {
          this.submitButtonPrice.textContent = this.formatMoney(totalPrice);
        }

        if (this.submitButtonCompare) {
          const shouldShowCompare = totalComparePrice > totalPrice;
          this.submitButtonCompare.textContent = shouldShowCompare ? this.formatMoney(totalComparePrice) : '';
          this.submitButtonCompare.hidden = !shouldShowCompare;
        }
      }

      updateSubmitButtonCopy() {
        if (!this.submitButton || !this.submitButtonLabel) return;

        const defaultLabel = this.submitButton.dataset.tdButtonLabelDefault || '';
        const soldOutLabel = this.submitButton.dataset.tdButtonLabelSoldOut || defaultLabel;
        this.submitButtonLabel.textContent = this.submitButton.disabled ? soldOutLabel : defaultLabel;
      }

      syncFormSellingPlan() {
        if (!this.form) return;

        this.form.querySelectorAll('input[name="selling_plan"]').forEach((input) => input.remove());

        if (this.dataset.purchaseType !== 'subscription') return;

        const selectedPlanId = this.planSelect?.value;
        if (!selectedPlanId) return;

        const sellingPlanInput = document.createElement('input');
        sellingPlanInput.type = 'hidden';
        sellingPlanInput.name = 'selling_plan';
        sellingPlanInput.value = selectedPlanId;
        sellingPlanInput.setAttribute('data-td-selling-plan', 'true');
        this.form.appendChild(sellingPlanInput);
      }
    }
  );
}
